export interface RecoverableSession {
  readonly id: string;
  /** Re-attach the strategy and replay state (slice/camera) onto the rebuilt engine. */
  recover(): Promise<void>;
  /** Swap this pane to the CPU-2D strategy (runtime downgrade after repeated GPU loss). */
  downgrade(): Promise<void>;
}

export interface ContextLossRecoveryDeps {
  sessions: () => RecoverableSession[];
  /** Rebuild the shared rendering engine after a loss (real impl recreates it; tests fake it). */
  rebuildEngine: () => Promise<void> | void;
  /** Drive the "recovering" overlay / observable state. */
  onStatusChange: (status: "ok" | "recovering") => void;
  onLog: (level: "info" | "warn" | "error", code: string, detail: Record<string, unknown>) => void;
  /** Consecutive losses before a pane is downgraded to Cpu2d (default 2). */
  downgradeThreshold?: number;
}

export class ContextLossRecovery {
  private losses = 0;
  private recovering = false;

  constructor(private deps: ContextLossRecoveryDeps) {}

  /** webglcontextlost handler. Returns true → caller should preventDefault() to allow restoration. */
  onLost(): boolean {
    this.losses += 1;
    this.recovering = true;
    this.deps.onStatusChange("recovering");
    this.deps.onLog("warn", "webgl_context_lost", { losses: this.losses });
    return true;
  }

  /** webglcontextrestored handler (or a re-init timeout). */
  async onRestored(): Promise<void> {
    if (!this.recovering) return;
    await this.deps.rebuildEngine();
    const threshold = this.deps.downgradeThreshold ?? 2;
    const sessions = this.deps.sessions();
    if (this.losses >= threshold) {
      this.deps.onLog("warn", "webgl_persistent_loss_downgrade", { losses: this.losses });
      for (const s of sessions) await s.downgrade();
      this.losses = 0; // reset only after a downgrade
    } else {
      for (const s of sessions) await s.recover();
    }
    this.recovering = false;
    this.deps.onStatusChange("ok");
  }
}
